import moment from "moment";

interface IRetryPolicyOptions {
    attemptTimings?: moment.Duration[];
    abortSignal?: AbortSignal;
}

// Retries 3 times, with the second attempt happening after 1 second after the first one fails and
// the third attempt happening after 5 seconds after the second one fails.
const defaultAttemptTimings = [moment.duration(1, "second"), moment.duration(5, "seconds")];

async function waitForTime(duration: moment.Duration, abortSignal?: AbortSignal) {
    return await new Promise((resolve, reject) => {
        const waitTimerHandle = setTimeout(resolve, duration.asMilliseconds());
        if (abortSignal) {
            abortSignal.addEventListener("abort", () => {
                waitTimerHandle && clearTimeout(waitTimerHandle);
                reject(new Error("Aborted"));
            });
        }
    });
}

/**
 * Retries the provided work function if the work function throws an error according
 * to the provided attempt timings.
 * @param work The work function to retry
 * @param options Options for the retry policy
 * @returns The result of the work function
 * @example
 * // Retries the API call 2 times (attemptTimes.length + 1).
 * // The second time occuring 1 second after the
 * // first attempt fails
 * await retryPolicy(() => fetch("https://example.com"), {
 *    attemptTimings: [moment.duration(1, "second")],
 *    abortSignal: abortController.signal,
 * });
 */
export async function retryPolicy<ReturnType>(
    work: () => Promise<ReturnType>,
    { attemptTimings = defaultAttemptTimings, abortSignal }: IRetryPolicyOptions,
) {
    const timingStack = [...attemptTimings].reverse();

    while (true) {
        try {
            abortSignal?.throwIfAborted();
            return await work();
        } catch (e) {
            const nextDelay = timingStack.pop();
            if (nextDelay) {
                await waitForTime(nextDelay, abortSignal);
            } else {
                throw e;
            }
        }
    }
}
